import type {
  CredentialMaterial,
  CredentialRequest,
  CredentialResolver,
  HttpCredentialMaterial,
  WebSocketCredentialMaterial,
} from './dataProviders.js';

function normalizeOrigins(values: readonly string[], protocols: readonly string[]): string[] {
  if (!values.length) throw new Error('A credential must allow at least one endpoint origin');
  return [...new Set(values.map(value => {
    try {
      const url = new URL(value);
      if (!protocols.includes(url.protocol) || url.origin === 'null') throw new Error();
      return url.origin;
    } catch {
      throw new Error(`Credential origin '${value}' is not an allowed absolute URL origin`);
    }
  }))];
}

function cloneCredential(credential: CredentialMaterial): CredentialMaterial {
  if (credential.kind === 'websocket-bearer') {
    return { ...credential, allowedOrigins: [...credential.allowedOrigins] };
  }
  return {
    ...credential,
    headers: { ...credential.headers },
    allowedOrigins: [...credential.allowedOrigins],
  };
}

/** Host-owned, memory-only credentials. Nothing here is serialized into a document. */
export class DataSourceCredentialStore {
  private readonly credentials = new Map<string, CredentialMaterial>();

  readonly resolve: CredentialResolver = async (request: CredentialRequest) => {
    const credential = this.credentials.get(request.reference);
    if (!credential || !credential.allowedOrigins.includes(request.origin)) return null;
    return cloneCredential(credential);
  };

  setWebSocketBearer(reference: string, token: string, allowedOrigins: readonly string[]): void {
    this.validateReference(reference);
    if (!token) throw new Error('A WebSocket bearer token cannot be empty');
    const credential: WebSocketCredentialMaterial = {
      kind: 'websocket-bearer',
      token,
      allowedOrigins: normalizeOrigins(allowedOrigins, ['ws:', 'wss:']),
    };
    this.credentials.set(reference, credential);
  }

  setHttpHeaders(
    reference: string,
    headers: Readonly<Record<string, string>>,
    allowedOrigins: readonly string[],
  ): void {
    this.validateReference(reference);
    if (!Object.keys(headers).length) throw new Error('An HTTP credential must contain at least one header');
    const credential: HttpCredentialMaterial = {
      kind: 'http-headers',
      headers: { ...headers },
      allowedOrigins: normalizeOrigins(allowedOrigins, ['http:', 'https:']),
    };
    this.credentials.set(reference, credential);
  }

  clear(reference: string): void {
    this.credentials.delete(reference);
  }

  clearAll(): void {
    this.credentials.clear();
  }

  private validateReference(reference: string): void {
    if (!reference) throw new Error('A credential profile reference cannot be empty');
  }
}
